import { useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import type { CredentialResponse } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

export default function Login() {
  const { loginWithPassword, loginWithGoogle, loginWithAmazon, verifyMfa, requires2fa } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("session") === "expired";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const AMAZON_SSO_ENABLED = process.env.REACT_APP_AMAZON_SSO_ENABLED === "true";

  if (!GOOGLE_CLIENT_ID) {
    console.error("Missing REACT_APP_GOOGLE_CLIENT_ID environment variable");
  }

  const navigateAfterLogin = () => {
    const token = localStorage.getItem("tv_token");
    if (token) {
      try {
        const decoded = jwtDecode<{ is_admin: boolean }>(token);
        navigate(decoded.is_admin ? "/admin/dashboard" : "/app/dashboard");
      } catch {
        navigate("/app/dashboard");
      }
    } else {
      navigate("/app/dashboard");
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await loginWithPassword(username, password);
      // If requires2fa was set, we stay on the login page showing the TOTP input
      // Otherwise navigate to dashboard
      if (!localStorage.getItem("tv_token")) {
        // 2FA challenge — stay on page, loading will stop
        setLoading(false);
        return;
      }
      navigateAfterLogin();
    } catch (error: any) {
      setErr(error.message || "Login failed");
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await verifyMfa(totpCode, useRecovery);
      navigateAfterLogin();
    } catch (error: any) {
      setErr(error.message || "Verification failed");
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (cred: CredentialResponse) => {
    setErr("");
    setLoading(true);
    if (!cred.credential) {
      setErr("Google login failed: No credential received");
      setLoading(false);
      return;
    }
    try {
      await loginWithGoogle(cred.credential);
      if (!localStorage.getItem("tv_token")) {
        setLoading(false);
        return;
      }
      navigateAfterLogin();
    } catch (error: any) {
      setErr(error.message || "Google login failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-900 flex flex-col md:flex-row">
      {/* Left panel - brand visual */}
      <div className="hidden md:flex md:w-1/2 bg-surface-800 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Logo and tagline */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          <img
            src="/quantum-small.png"
            alt="Quantum Energy"
            className="w-24 h-24 mb-8"
          />
          <h2 className="text-2xl font-bold text-text-primary mb-4">
            TotalView Platform
          </h2>
          <p className="text-text-secondary text-center max-w-sm">
            Quantify the environmental and economic impacts of your renewable energy projects
          </p>

          {/* Feature highlights */}
          <div className="mt-12 space-y-4">
            <FeatureItem icon={<LeafIcon />} text="Life Cycle Assessment (LCA)" />
            <FeatureItem icon={<ChartIcon />} text="Impact quantification" />
            <FeatureItem icon={<ReportIcon />} text="ISO 14044 compliant reports" />
          </div>
        </div>
      </div>

      {/* Vertical divider */}
      <div className="hidden md:block w-px bg-border" />

      {/* Right panel - login form */}
      <div className="relative flex flex-1 flex-col items-center justify-center p-6 md:p-12">
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-surface-900/80 backdrop-blur-sm">
            <LoadingSpinner />
          </div>
        )}

        {/* Mobile logo */}
        <div className="md:hidden mb-8 flex flex-col items-center">
          <img
            src="/quantum-small.png"
            alt="Quantum Energy"
            className="w-16 h-16 mb-4"
          />
        </div>

        {/* Title */}
        <h1 className="mb-2 text-center text-h1 text-text-primary">
          Welcome back
        </h1>
        <p className="mb-8 text-center text-body text-text-secondary">
          Sign in to your TotalView account
        </p>

        <div className="w-full max-w-sm">
          {/* Session expired notice */}
          {sessionExpired && !err && (
            <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30">
              <p className="text-body-sm text-health-400 text-center">Your session has expired. Please sign in again.</p>
            </div>
          )}

          {/* Error message */}
          {err && (
            <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30">
              <p className="text-body-sm text-health-400 text-center">{err}</p>
            </div>
          )}

          {requires2fa ? (
            /* 2FA verification step */
            <form className="space-y-4" onSubmit={handleMfaVerify}>
              <div className="text-center mb-4">
                <div className="inline-flex p-3 rounded-full bg-quantum-500/10 mb-3">
                  <svg className="w-6 h-6 text-quantum-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <p className="text-body-sm text-text-secondary">
                  {useRecovery
                    ? "Enter one of your recovery codes"
                    : "Enter the 6-digit code from your authenticator app"
                  }
                </p>
              </div>
              <div>
                <input
                  id="totp"
                  value={totpCode}
                  onChange={(e) => setTotpCode(
                    useRecovery
                      ? e.target.value.toLowerCase()
                      : e.target.value.replace(/\D/g, "")
                  )}
                  maxLength={useRecovery ? 8 : 6}
                  placeholder={useRecovery ? "recovery code" : "000000"}
                  className={`input text-center ${useRecovery ? 'font-mono' : 'text-2xl font-mono tracking-[0.5em]'}`}
                  disabled={loading}
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <button
                type="submit"
                disabled={loading || totpCode.length < (useRecovery ? 8 : 6)}
                className="btn-primary w-full py-3"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  "Verify"
                )}
              </button>
              <button
                type="button"
                onClick={() => { setUseRecovery(!useRecovery); setTotpCode(""); setErr(""); }}
                className="w-full text-center text-body-sm text-quantum-400 hover:text-quantum-300"
              >
                {useRecovery ? "Use authenticator code instead" : "Use a recovery code"}
              </button>
            </form>
          ) : (
            /* Normal login form */
            <>
              <form className="space-y-4" onSubmit={handlePasswordLogin}>
                <div>
                  <label htmlFor="username" className="input-label">
                    Username or Email
                  </label>
                  <input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    className="input"
                    disabled={loading}
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="input-label">
                    Password
                  </label>
                  <input
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    placeholder="Enter your password"
                    className="input"
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>
                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-body-sm text-quantum-400 hover:text-quantum-300">
                    Forgot password?
                  </Link>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-3"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    "Sign in"
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="my-6 flex items-center">
                <div className="flex-grow border-t border-border" />
                <span className="mx-4 text-caption text-text-muted">or continue with</span>
                <div className="flex-grow border-t border-border" />
              </div>

              {/* SSO Options */}
              <div className="space-y-3">
                {/* Amazon Federate SSO (prod only) */}
                {AMAZON_SSO_ENABLED && <button
                  type="button"
                  onClick={async () => {
                    setErr("");
                    setLoading(true);
                    try {
                      await loginWithAmazon();
                    } catch (e: any) {
                      setErr(e.message || "Amazon login failed");
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border border-border bg-surface-600 hover:bg-surface-500 transition-colors disabled:opacity-50"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.685zm3.186 7.705a.66.66 0 01-.753.072c-1.06-.878-1.246-1.287-1.827-2.124-1.748 1.782-2.986 2.315-5.249 2.315C6.871 18.058 5 16.553 5 13.77c0-2.164 1.172-3.636 2.845-4.357 1.452-.638 3.479-.752 5.03-.928v-.344c0-.637.049-1.393-.325-1.944-.325-.49-.949-.693-1.502-.693-1.02 0-1.931.524-2.153 1.608-.046.24-.22.479-.458.491l-2.571-.276c-.216-.048-.456-.222-.394-.553C5.968 4.187 8.321 3.2 10.423 3.2c1.075 0 2.48.286 3.327 1.099.893.942.807 2.199.807 3.567v3.231c0 .971.403 1.398.782 1.923.134.186.163.41-.008.548-.428.356-1.188 1.02-1.607 1.393l-.58-.167z" fill="#FF9900"/>
                    <path d="M21.543 17.846C20.036 19.025 17.824 19.65 15.89 19.65c-2.73 0-5.186-1.01-7.044-2.69-.146-.132-.016-.312.16-.21 2.005 1.167 4.485 1.868 7.046 1.868 1.728 0 3.627-.358 5.375-1.1.264-.112.485.173.116.328z" fill="#FF9900"/>
                    <path d="M22.322 16.942c-.199-.254-1.316-.12-1.818-.06-.152.018-.176-.114-.038-.21 .89-.626 2.351-.446 2.522-.236.172.213-.045 1.686-.88 2.39-.128.108-.25.05-.194-.093.188-.47.608-1.536.408-1.79z" fill="#FF9900"/>
                  </svg>
                  <span className="text-body-sm font-medium text-text-primary">Continue with Amazon</span>
                </button>}

                {/* Google OAuth */}
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setErr("Google login failed")}
                      width={380}
                      text="continue_with"
                      size="large"
                      logo_alignment="center"
                      theme="filled_black"
                    />
                  </div>
                </GoogleOAuthProvider>
              </div>
            </>
          )}

          {/* Footer */}
          <p className="mt-8 text-center text-caption text-text-muted">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}

// Feature item component
function FeatureItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-3 text-text-secondary">
      <div className="p-2 rounded-lg bg-quantum-500/10 text-quantum-400">
        {icon}
      </div>
      <span className="text-body-sm">{text}</span>
    </div>
  );
}

// Loading spinner
function LoadingSpinner({ size = "md" }: { size?: "sm" | "md" }) {
  const sizeClass = size === "sm" ? "w-5 h-5" : "w-8 h-8";
  return (
    <svg
      className={`animate-spin ${sizeClass} text-quantum-500`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// Icons
function LeafIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
